import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { HERO, SECTORS, BRAND, BRANDS } from "@/data/media";
import { Seo } from "@/components/Seo";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const sectorPreview = [
  { key: "hotel", img: SECTORS.hotel },
  { key: "realEstate", img: SECTORS.realEstate },
  { key: "ristoranti", img: SECTORS.ristoranti },
  { key: "outdoor", img: SECTORS.outdoor },
];

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      <Seo page="home" />
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <img src={HERO.home} alt="Sofas Contract" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-20 md:pb-28 w-full">
          <Reveal delay={120}>
            <h1 className="font-serif-display font-light text-white text-5xl sm:text-6xl md:text-8xl leading-[0.92] tracking-tight max-w-4xl">
              {t.home.heroTitle}
            </h1>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-8 max-w-2xl text-white text-2xl md:text-3xl leading-relaxed font-light">
              {t.home.heroSubtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="overline mb-5">{t.home.introOverline}</p>
              <h2 className="font-serif-display font-light text-4xl md:text-5xl leading-tight tracking-tight text-ink max-w-2xl">
                {t.home.introTitle}
              </h2>
              <p className="mt-8 text-ink/70 text-lg leading-relaxed max-w-2xl">{t.home.introText}</p>
              <Link
                to="/chi-siamo"
                data-testid="home-about-link"
                className="mt-8 inline-flex items-center gap-2 text-ink font-medium border-b-2 border-brass pb-1 hover:gap-3 transition-all"
              >
                {t.common.discover} <ArrowRight className="w-4 h-4 text-brass" />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={200}>
              <div className="relative">
                <img src={BRAND.logoFull} alt="Sofas Contract" className="w-full max-w-xs mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* STATS */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 md:mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-brass/20">
            {t.home.stats.map((s, i) => (
              <Reveal key={i} delay={i * 100} className="border-b md:border-b-0 border-r border-brass/20 last:border-r-0 py-8 px-4">
                <div className="font-serif-display text-4xl md:text-5xl text-brass">{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.15em] text-ink/60">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-20 md:py-32 bg-sand">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal className="max-w-2xl">
              <p className="overline mb-4">{t.nav.sectors}</p>
              <h2 className="font-serif-display font-light text-4xl md:text-5xl tracking-tight text-ink">
                {t.home.sectorsTitle}
              </h2>
              <p className="mt-5 text-ink/65 leading-relaxed">{t.home.sectorsSubtitle}</p>
            </Reveal>
            <Link
              to="/settori"
              data-testid="home-sectors-link"
              className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-wide font-medium text-ink hover:text-brass transition-colors"
            >
              {t.home.viewAll} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {sectorPreview.map((s, i) => {
              const item = t.sectors.items.find((it) => it.key === s.key);
              return (
                <Reveal key={s.key} delay={i * 90}>
                  <Link to="/settori" data-testid={`home-sector-${s.key}`} className="group block relative aspect-[3/4] overflow-hidden bg-ink">
                    <img src={s.img} alt={item?.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[900ms]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                    <div className="absolute bottom-0 p-6">
                      <h3 className="font-serif-display text-2xl text-white">{item?.title}</h3>
                      <span className="mt-2 inline-block w-8 h-px bg-brass group-hover:w-14 transition-all duration-500" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS — Abbiamo prodotto per */}
      <section className="py-20 md:py-32 bg-cream" data-testid="home-brands">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-12">
            <p className="overline mb-4">{t.common.madeInItaly}</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl tracking-tight text-ink">
              {t.home.brandsTitle}
            </h2>
            <p className="mt-5 text-ink/65 leading-relaxed">{t.home.brandsSubtitle}</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {BRANDS.map((b, i) => (
              <Reveal key={b.name} delay={i * 80}>
                <div
                  data-testid={`home-brand-${i}`}
                  className="group flex items-center justify-center bg-white border border-brass/15 h-28 md:h-32 px-6 transition-all duration-500 hover:border-brass/40 hover:shadow-md"
                >
                  <img
                    src={b.logo}
                    alt={b.name}
                    className="h-14 md:h-16 w-auto object-contain opacity-70 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-24 md:py-36 bg-ink text-white overflow-hidden">
        <img src={SECTORS.palazzi} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="overline mb-6">{t.common.madeInItaly}</p>
            <h2 className="font-serif-display font-light text-4xl md:text-6xl leading-tight tracking-tight">
              {t.home.ctaBandTitle}
            </h2>
            <p className="mt-8 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">{t.home.ctaBandText}</p>
            <Link
              to="/contatti"
              data-testid="home-cta-band-link"
              className="mt-10 inline-flex items-center gap-2 bg-brass text-white px-10 py-4 text-sm tracking-wide uppercase font-medium hover:bg-brass-dark transition-colors duration-300"
            >
              {t.common.requestQuote} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
